import { InboxOutlined } from "@ant-design/icons";
import { Button, Modal, Typography, Upload, message } from "antd";
import { RcFile } from "antd/lib/upload";
import { useUploadFileToS3Mutation } from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useState } from "react";
import excelService from "../../services/excel/excelService";
import logger from "../../services/logger/logger";
import extractExcelBase64FromJson from "../../utils/excelUtils";

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

interface S3UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

interface ExcelReuploadModalProps {
  visible: boolean;
  errorMessage: string | null;
  onReupload: (fileData: {
    name: string;
    type: string;
    content: string;
    consumptionFileURL?: string;
    originalData?: any;
    jsonData?: any;
    s3Upload?: S3UploadResult;
    occupancy_data?: boolean;
    isReuploaded?: boolean;
  }) => void;
  onCancel: () => void;
  isFromConsumptionDataReupload?: boolean;
}

const ExcelReuploadModal: React.FC<ExcelReuploadModalProps> = ({
  visible,
  errorMessage,
  onReupload,
  // Keep the onCancel in the destructuring for compatibility,
  // although we're not using it in this component anymore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCancel,
  isFromConsumptionDataReupload = false,
}) => {
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadFileToS3Mutation] = useUploadFileToS3Mutation();

  const handleFileUpload = (file: RcFile) => {
    // Only accept Excel files
    const isExcel =
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls");

    if (!isExcel) {
      message.error("You can only upload Excel files!");
      return false;
    }

    // Limit file size to 10MB
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error("File must be smaller than 10MB!");
      return false;
    }

    // Add file to list
    setFileList([file]);
    return false; // Prevent automatic upload
  };

  const uploadFileToS3 = async (excelData: { base64Content: string; fileName: string; fileType: string }) => {
    if (!excelData || !excelData.base64Content) {
      logger.error("No valid file data to upload to S3");
      return {
        success: false,
        error: "No valid file data to upload",
      };
    }

    try {
      // Call the GraphQL mutation
      const { data } = await uploadFileToS3Mutation({
        variables: {
          input: {
            file: excelData.base64Content,
            fileName: excelData.fileName,
            fileType: excelData.fileType,
          },
        },
      });
      const url = data?.uploadFileToS3;
      const key = url ? url.split("/").slice(-2).join("/") : undefined;
      const result = {
        success: !!url,
        url,
        key,
      };
      message.success("File uploaded to S3 successfully");
      return result;
    } catch (err) {
      logger.error("Error uploading file to S3:", err);
      const result = {
        success: false,
        error: err instanceof Error ? err.message : "Unknown error uploading to S3",
      };
      message.error("Failed to upload file to S3");
      return result;
    }
  };

  const handleReupload = async () => {
    if (fileList.length === 0) {
      message.warning("Please select a file first");
      return;
    }

    setUploading(true);

    try {
      const file = fileList[0];

      // Helper function to read a file as base64
      const readFileAsBase64 = (fileObj: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = (e) => {
            if (e.target?.result) {
              // Extract base64 content from the data URL
              const base64Content = e.target.result.toString();
              resolve(base64Content);
            } else {
              reject(new Error("Failed to read file content"));
            }
          };

          reader.onerror = () => {
            logger.error("Error reading file");
            reject(new Error("Failed to read file"));
          };

          // Read the file as data URL (base64)
          reader.readAsDataURL(fileObj);
        });
      };

      // Convert the file to a JSON object with the structure expected by extractExcelBase64FromJson
      const jsonData = {
        data: {
          location_consumption: [
            {
              name: file.name,
              // We need to read the file content first
              content: await readFileAsBase64(file),
              type: file.type,
            },
          ],
        },
      };

      // Extract the base64 content using the same function as ExcelDataExtractor
      const excelData = extractExcelBase64FromJson(jsonData);

      if (!excelData) {
        message.error("Failed to extract Excel data from file");
        setUploading(false);
        return;
      }

      // Extract data from the Excel file
      let extractedData = null;
      try {
        extractedData = await excelService.extractDataFromExcel(excelData);
      } catch (error) {
        logger.error("Error extracting data from Excel:", error);
        message.error("Failed to process Excel file structure");
        setUploading(false);
        return;
      }

      // Upload the file to S3
      const s3Result = await uploadFileToS3(excelData);

      // Call the onReupload callback with the file data and S3 upload result
      onReupload({
        name: excelData.fileName,
        type: excelData.fileType,
        content: excelData.base64Content,
        consumptionFileURL: s3Result.success && "url" in s3Result ? s3Result?.url : undefined,
        originalData: extractedData, // Add the extracted Excel data
        jsonData, // Add the original JSON data
        s3Upload: s3Result || undefined,
        occupancy_data: true, // Match the format from ExcelDataExtractor
        isReuploaded: true, // Add flag to indicate this is a reuploaded file
      });

      setFileList([]);
    } catch (error) {
      logger.error("Error processing file:", error);
      message.error("Failed to process the selected file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title={isFromConsumptionDataReupload ? "Reupload Consumption File" : "Excel File Validation Error"}
      open={visible}
      closable={isFromConsumptionDataReupload}
      maskClosable={isFromConsumptionDataReupload}
      keyboard={isFromConsumptionDataReupload}
      onCancel={isFromConsumptionDataReupload ? onCancel : undefined}
      footer={[
        ...(isFromConsumptionDataReupload
          ? [
              <Button key="cancel" onClick={onCancel}>
                Cancel
              </Button>,
            ]
          : []),
        <Button
          key="reupload"
          type="primary"
          onClick={handleReupload}
          loading={uploading}
          disabled={fileList.length === 0}
        >
          Upload Valid File
        </Button>,
      ]}
      width={600}
    >
      <div className="excel-reupload-modal">
        <Typography>
          {isFromConsumptionDataReupload ? (
            <>
              <Paragraph>Please upload a valid Excel file following the required template format:</Paragraph>
              <ul>
                <li>Single worksheet only</li>
                <li>Required columns for consumption data</li>
                <li>
                  Valid numerical values in data cells (no units like &ldquo;m³&rdquo; or text like
                  &ldquo;1000abc&rdquo;)
                </li>
                <li>Proper date formats</li>
              </ul>
              <Paragraph>
                <Text strong>You must upload a valid file to continue.</Text>
              </Paragraph>
            </>
          ) : (
            <>
              <Title level={5}>The Excel file you uploaded has validation errors:</Title>
              <Paragraph>
                <Text type="danger">{errorMessage}</Text>
              </Paragraph>
              <Paragraph>Please upload a valid Excel file following the required template format:</Paragraph>
              <ul>
                <li>Single worksheet only</li>
                <li>Required columns for consumption data</li>
                <li>
                  Valid numerical values in data cells (no units like &ldquo;m³&rdquo; or text like
                  &ldquo;1000abc&rdquo;)
                </li>
                <li>Proper date formats</li>
              </ul>
              <Paragraph>
                <Text strong>You must upload a valid file to continue.</Text>
              </Paragraph>
            </>
          )}
        </Typography>

        <Dragger
          name="file"
          multiple={false}
          fileList={fileList}
          beforeUpload={handleFileUpload}
          onRemove={() => setFileList([])}
          accept=".xlsx,.xls"
          style={{ marginTop: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
          </p>
          <p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
          <p className="ant-upload-hint">Support for single Excel file only (.xlsx or .xls)</p>
        </Dragger>
      </div>
    </Modal>
  );
};

export default ExcelReuploadModal;
